import { registerDestructor } from '@ember/destroyable';
import { guidFor } from '@ember/object/internals';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { EmberChangeset } from 'ember-changeset';
import { validatePresence } from 'ember-changeset-validations/validators';
import type { ValidationResult, ValidatorMapFunc } from 'ember-changeset/types';
import { type MetaData } from 'ember-phone-input/components/phone-input';
import { or } from 'ember-truth-helpers';
import validateSometimes from '../../utils/sometimes.ts';
import Phone from '../phone.gts';
import ValidatedField from '../validated-field.gts';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    changeset: EmberChangeset;
    propertyName: string;
    isTestPhoneNumber?: (phone: string | null) => boolean;
    errorClass?: string;
    isReadOnly?: boolean;
    dataTestInput?: string;
    labelName?: string;
    isMandatory?: boolean;
    placeholder?: string;
    // Function that creates additional validations with access to ChangesetPhone context
    createAdditionalValidations?: (phone: ChangesetPhone) => ValidatorMapFunc[];
    onChangesetUpdate?: (changeset: EmberChangeset) => void;
  };
}

export default class ChangesetPhone extends Component<Signature> {
  @tracked declare metadata: MetaData;

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);

    const validationMap = this.args.changeset.validationMap;
    if (!validationMap) {
      return;
    }

    if (validationMap[this.args.propertyName]) {
      if (!Array.isArray(validationMap[this.args.propertyName])) {
        validationMap[this.args.propertyName] = [
          validationMap[this.args.propertyName]
        ];
      }
      (validationMap[this.args.propertyName] as ValidatorMapFunc[]).push(
        ...this.phoneValidationFunctions
      );
    } else {
      validationMap[this.args.propertyName] = this.phoneValidationFunctions;
    }

    /** Captured now: when the route exits, the `changeset` arg is already undefined at destroy time. */
    const propertyName = this.args.propertyName;
    registerDestructor(this, () => {
      if (validationMap[propertyName]) {
        delete validationMap[propertyName];
      }
    });
  }

  setPhone = (value: string | null, metadata: MetaData) => {
    this.metadata = metadata;

    if (!this.isSettingInitialNullValue(this.args.changeset, value)) {
      this.args.changeset.set(this.args.propertyName, value);
    }

    this.args.onChangesetUpdate?.(this.args.changeset);
  };

  get phone(): string | null {
    return this.args.changeset.get(this.args.propertyName) as string | null;
  }

  get dialCode(): string | undefined {
    return (this.metadata.selectedCountryData as { dialCode: string })
      ?.dialCode;
  }

  get preferredCountriesWithPrefixes(): {
    country: string;
    prefix: string;
    mobilePrefixes: string[];
  }[] {
    return [
      // France must stay at the top of the list, as it's the default country
      { country: 'fr', prefix: '33', mobilePrefixes: ['+336', '+337'] },
      { country: 'gp', prefix: '590', mobilePrefixes: ['+590690', '+590691'] },
      { country: 'mq', prefix: '596', mobilePrefixes: ['+596696', '+596697'] },
      { country: 'gf', prefix: '594', mobilePrefixes: ['+594694'] },
      { country: 're', prefix: '262', mobilePrefixes: ['+262692', '+262693'] },
      { country: 'yt', prefix: '269', mobilePrefixes: ['+269639'] },
      { country: 'pm', prefix: '508', mobilePrefixes: ['+508'] },
      { country: 'bl', prefix: '590', mobilePrefixes: ['+590690', '+590691'] },
      { country: 'mf', prefix: '590', mobilePrefixes: ['+590690', '+590691'] },
      { country: 'wf', prefix: '681', mobilePrefixes: ['+68182'] },
      { country: 'pf', prefix: '689', mobilePrefixes: ['+68987', '+689689'] },
      { country: 'nc', prefix: '687', mobilePrefixes: ['+687687'] }
    ];
  }

  get preferredCountries(): string[] {
    return this.preferredCountriesWithPrefixes.map(({ country }) => country);
  }

  // France & DOM-TOM
  get allowedPrefixes(): Set<string> {
    return new Set(
      this.preferredCountriesWithPrefixes.map(({ prefix }) => prefix)
    );
  }

  // Given a phone number and a dial code, check if the phone number is a mobile number
  // Only working for France et DOM-TOM
  get isMobileGivenDialCode(): boolean {
    if (!this.dialCode || !this.phone) {
      return false;
    }

    if (
      !this.preferredCountriesWithPrefixes.some(
        ({ prefix }) => prefix === this.dialCode
      )
    ) {
      // If the dial code is not part of the preferred countries, we can't compute if it's a mobile
      return false;
    }

    return this.preferredCountriesWithPrefixes.some(({ mobilePrefixes }) =>
      mobilePrefixes.some(mobilePrefix => this.phone?.startsWith(mobilePrefix))
    );
  }

  // Phone number is invalid and no dial code is selected
  get dialCodeIsInvalid(): boolean {
    return (
      !this.metadata.isValidNumber &&
      !(this.metadata.selectedCountryData as { dialCode: string })?.dialCode
    );
  }

  // Phone number is valid but the dial code is not part of the allowed prefixes
  get phoneIsntPartOfAllowedDialCodes(): boolean {
    return (
      this.metadata.isValidNumber &&
      Boolean(this.dialCode) &&
      !this.allowedPrefixes.has(this.dialCode as string)
    );
  }

  get phoneValidationFunctions(): ValidatorMapFunc[] {
    const validations: ValidatorMapFunc[] = [
      validateSometimes(
        (): ValidationResult => {
          if (this.args.isTestPhoneNumber?.(this.phone)) {
            return true;
          }
          if (!this.metadata.isValidNumber) {
            if (this.dialCodeIsInvalid) {
              return "L'indicatif téléphonique inscrit n'est pas reconnu";
            }
            return 'Le numéro de téléphone est invalide';
          }
          return true;
        },
        () => {
          return Boolean(this.args.changeset.get(this.args.propertyName));
        }
      ),
      validateSometimes(
        validatePresence({
          presence: true,
          message: 'Le numéro de téléphone ne peux être vide'
        }),
        () => {
          return Boolean(this.args.isMandatory);
        }
      )
    ];

    // Add additional validations from parent component if provided
    if (this.args.createAdditionalValidations) {
      const additionalValidations = this.args.createAdditionalValidations(this);
      validations.push(...additionalValidations);
    }

    return validations;
  }

  get inputId(): string {
    return guidFor(this);
  }

  private isSettingInitialNullValue(
    changeset: EmberChangeset,
    value: string | null
  ): boolean {
    return (
      changeset.changes.length == 0 &&
      value === null &&
      !changeset.get(this.args.propertyName)
    );
  }

  <template>
    <ValidatedField
      @propertyName={{@propertyName}}
      @changeset={{@changeset}}
      @errorClass={{or @errorClass 'error-validated-input-display'}}
    >
      <label
        for={{this.inputId}}
        data-test-label={{or @dataTestInput @propertyName}}
      >
        {{@labelName}}{{if @isMandatory ' *'}}
      </label>
      <Phone
        @preferredCountries={{this.preferredCountries}}
        @phone={{this.phone}}
        @setPhone={{this.setPhone}}
        @separateDialCode={{true}}
        @disabled={{@isReadOnly}}
        @placeholder={{@placeholder}}
        @name={{@propertyName}}
        data-test-input={{or @dataTestInput @propertyName}}
      />
    </ValidatedField>
  </template>
}
